import { BusinessObjectBase } from './base';
import { roleSchema } from '@schemas/business-objects/role';
import type { RoleAssignment } from './role-assignment';
import type { Id } from '../base-types';

export interface RoleData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly roleAssignments?: RoleAssignment[];
}

export interface Role extends Readonly<RoleData> {}
type RoleCreateWithChildrenData = Partial<RoleData>;

export class Role extends BusinessObjectBase {
  static readonly entityName = 'Role' as const;
  static readonly plural = 'roles';
  static schema = roleSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Role[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new Role(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Role | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new Role(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new Role(data);
  }

  static async create(data: Omit<RoleData, 'id'>): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Role(result);
  }

  static async createMany(data: Omit<RoleData, 'id'>[]): Promise<Role[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Role(item));
  }

  static async createWithChildren(
    data: RoleCreateWithChildrenData,
  ): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Role(result);
  }

  static async createManyWithChildren(
    data: RoleCreateWithChildrenData[],
  ): Promise<Role[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Role(item));
  }

  static async updateById(id: Id, updates: Partial<RoleData>): Promise<Role> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Role(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
